import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  isWildcardToken,
  SuggestionSelectHandler,
} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchTypes';
import {
  InboxesSearchSuggestionsBaseProps,
  InboxesSuggestionsDropdownItemIcon,
  InboxesSuggestionsDropdownItemPrimaryContent,
  InboxesSuggestionsDropdownItemSecondaryContent,
  InboxesSuggestionsDropdownModifierItem,
  InboxesSuggestionsDropdownSection,
  InboxesSuggestionsDropdownSectionTitle,
  InboxesSuggestionsDropdownSectionWrapper,
} from './inboxesSearchSuggestionsDropdownCommons';

/*
 * Constants.
 */

const searchModifiersSections: ReadonlyArray<ModifierSection> = [
  {
    title: <Trans id="search-header-inbox">Inbox</Trans>,
    icon: 'inboxSmall',
    modifiers: [[ModifiersEnum.INBOX, <Trans id="search-modifier-inbox">inbox:</Trans>]],
  },
  {
    title: <Trans id="search-header-message">Message</Trans>,
    icon: 'messageSmall',
    modifiers: [
      [ModifiersEnum.RECIPIENT, <Trans id="search-modifier-recipient">recipient:</Trans>],
      [ModifiersEnum.FROM, <Trans id="search-modifier-from">from:</Trans>],
      [ModifiersEnum.TO, <Trans id="search-modifier-to">to:</Trans>],
      [ModifiersEnum.CC, <Trans id="search-modifier-cc">cc:</Trans>],
      [ModifiersEnum.BCC, <Trans id="search-modifier-bcc">bcc:</Trans>],
      [
        ModifiersEnum.SUBJECT,
        <Trans id="search-modifier-subject">subject:</Trans>,
        <Trans id="search-modifier-subject-description">&quot;text&quot;</Trans>,
      ],
      [
        ModifiersEnum.BODY,
        <Trans id="search-modifier-body">body:</Trans>,
        <Trans id="search-modifier-body-description">&quot;text&quot;</Trans>,
      ],
    ],
  },
  {
    title: <Trans id="search-header-teammates">Teammates</Trans>,
    icon: 'teammateSmall',
    modifiers: [
      [ModifiersEnum.ASSIGNEE, <Trans id="search-modifier-assignee">assignee:</Trans>],
      [ModifiersEnum.AUTHOR, <Trans id="search-modifier-author">author:</Trans>],
      [ModifiersEnum.PARTICIPANT, <Trans id="search-modifier-participant">participant:</Trans>],
      [ModifiersEnum.MENTION, <Trans id="search-modifier-mention">mention:</Trans>],
      [ModifiersEnum.COMMENTER, <Trans id="search-modifier-commenter">commenter:</Trans>],
    ],
  },
  {
    title: <Trans id="search-header-properties">Properties</Trans>,
    icon: 'searchSmall',
    modifiers: [
      [
        ModifiersEnum.TYPE,
        <Trans id="search-modifier-type">type:</Trans>,
        <Trans id="search-modifier-type-description">email, tweet, sms, facebook...</Trans>,
      ],
      [
        ModifiersEnum.TAG,
        <Trans id="search-modifier-tag">tag:</Trans>,
        <Trans id="search-modifier-tag-description">tag_name</Trans>,
      ],
      [
        ModifiersEnum.ACCOUNT,
        <Trans id="search-modifier-account">account:</Trans>,
        <Trans id="search-modifier-tag-account">account_name</Trans>,
      ],
      [
        ModifiersEnum.HAS,
        <Trans id="search-modifier-has">has:</Trans>,
        <Trans id="search-modifier-has-description">reminder, attachment, no-tags...</Trans>,
      ],
      [
        ModifiersEnum.IS,
        <Trans id="search-modifier-is">is:</Trans>,
        <Trans id="search-modifier-is-description">unread, unreplied, open, closed...</Trans>,
      ],
      [
        ModifiersEnum.IN,
        <Trans id="search-modifier-in">in:</Trans>,
        <Trans id="search-modifier-in-description">comments, messages, attachments</Trans>,
      ],
      [
        ModifiersEnum.BEFORE,
        <Trans id="search-modifier-before">before:</Trans>,
        <Trans id="search-modifier-date-description">yyyy-mm-dd</Trans>,
      ],
      [
        ModifiersEnum.AFTER,
        <Trans id="search-modifier-after">after:</Trans>,
        <Trans id="search-modifier-date-description">yyyy-mm-dd</Trans>,
      ],
      [
        ModifiersEnum.DURING,
        <Trans id="search-modifier-during">during:</Trans>,
        <Trans id="search-modifier-date-description">yyyy-mm-dd</Trans>,
      ],
    ],
  },
];

/*
 * Props.
 */

interface ModifierSection {
  title: ReactNode;
  icon: IconName;
  modifiers: ReadonlyArray<[ModifiersEnum, ReactNode] | [ModifiersEnum, ReactNode, ReactNode]>;
}

interface InboxesSearchSuggestionsDropdownModifiersProps extends InboxesSearchSuggestionsBaseProps {
  hasSearchAccountFilter: boolean;
}

/*
 * Styles.
 */

const StyledSectionDiv = styled.div`
  padding: 0 0 15px 16px;
`;

const StyledHeaderDiv = styled.div`
  display: flex;
  padding-bottom: 6px;
  align-items: center;

  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
`;

const StyledModifiersDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: 18px;
`;

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdownModifiers: FC<
  InboxesSearchSuggestionsDropdownModifiersProps
> = ({query, activeToken, onSuggestionSelect, hasSearchAccountFilter}) => {
  if (!isWildcardToken(activeToken, query)) {
    return null;
  }

  return (
    <InboxesSuggestionsDropdownSectionWrapper>
      <InboxesSuggestionsDropdownSection>
        <InboxesSuggestionsDropdownSectionTitle>
          <Trans id="inboxes-advanced-filters">Advanced Filters</Trans>
        </InboxesSuggestionsDropdownSectionTitle>
      </InboxesSuggestionsDropdownSection>
      {searchModifiersSections.map((section) => (
        <StyledSectionDiv key={section.icon}>
          <StyledHeaderDiv>
            <InboxesSuggestionsDropdownItemIcon iconName={section.icon} />
            {section.title}
          </StyledHeaderDiv>
          <StyledModifiersDiv>
            {renderSectionModifiers(section, onSuggestionSelect, hasSearchAccountFilter)}
          </StyledModifiersDiv>
        </StyledSectionDiv>
      ))}
    </InboxesSuggestionsDropdownSectionWrapper>
  );
};

/*
 * Helpers.
 */

function renderSectionModifiers(
  section: ModifierSection,
  onSuggestionSelect: SuggestionSelectHandler,
  hasSearchAccountFilter: boolean,
) {
  const modifiers = hasSearchAccountFilter
    ? section.modifiers
    : section.modifiers.filter((modifier) => modifier[0] !== ModifiersEnum.ACCOUNT);

  return (
    <>
      {modifiers.map(([modifier, primaryContent, secondaryContent], index) =>
        renderSearchModifierItem(index, modifier, primaryContent, secondaryContent, onSuggestionSelect),
      )}
    </>
  );
}

function renderSearchModifierItem(
  index: number,
  modifier: ModifiersEnum,
  primaryContent: ReactNode,
  secondaryContent: ReactNode,
  onSuggestionSelect: SuggestionSelectHandler,
) {
  return (
    <InboxesSuggestionsDropdownModifierItem
      key={index}
      onSelect={(_e, key) =>
        onSuggestionSelect(`${modifier}:`, key, {insertWhiteSpace: false, modifierHelperType: modifier})
      }
    >
      <InboxesSuggestionsDropdownItemPrimaryContent>
        {primaryContent}
      </InboxesSuggestionsDropdownItemPrimaryContent>
      {secondaryContent && (
        <InboxesSuggestionsDropdownItemSecondaryContent>
          {secondaryContent}
        </InboxesSuggestionsDropdownItemSecondaryContent>
      )}
    </InboxesSuggestionsDropdownModifierItem>
  );
}
